/**
 * The nouns the discovery report, its scanning screens and its emails use for
 * the business and the people it serves, keyed on whether the check ran on the
 * healthcare path (`aeo_diagnostics.isHealthcare`, read with
 * `readIsHealthcare`).
 *
 * Verbatim from the approved copy deck ("Scan, report and email" noun table):
 *
 *   patients  | customers
 *   patient   | customer
 *   practice  | business
 *   practices | businesses
 *   "accepting new patients" | "accepting new customers"
 *
 * The healthcare column is the wording the report has always used, so a
 * healthcare report reads exactly as before. Capitalised forms are listed
 * rather than derived so every string a reader sees is in this file.
 *
 * "Practice Discovery Report" is "Discovery Report" for everyone; it is not
 * keyed, so it lives in {@link DISCOVERY_REPORT_TITLE}.
 */

export interface ReportVocabulary {
  readonly isHealthcare: boolean;
  readonly patients: 'patients' | 'customers' | 'pet owners';
  readonly patient: 'patient' | 'customer' | 'pet owner';
  readonly practice: 'practice' | 'business';
  readonly practices: 'practices' | 'businesses';
  readonly Patients: 'Patients' | 'Customers' | 'Pet owners';
  readonly Patient: 'Patient' | 'Customer' | 'Pet owner';
  readonly Practice: 'Practice' | 'Business';
  readonly Practices: 'Practices' | 'Businesses';
  readonly acceptingNew: 'accepting new patients' | 'accepting new customers';
}

const HEALTHCARE_VOCABULARY: ReportVocabulary = {
  isHealthcare: true,
  patients: 'patients',
  patient: 'patient',
  practice: 'practice',
  practices: 'practices',
  Patients: 'Patients',
  Patient: 'Patient',
  Practice: 'Practice',
  Practices: 'Practices',
  acceptingNew: 'accepting new patients',
};

const BUSINESS_VOCABULARY: ReportVocabulary = {
  isHealthcare: false,
  patients: 'customers',
  patient: 'customer',
  practice: 'business',
  practices: 'businesses',
  Patients: 'Customers',
  Patient: 'Customer',
  Practice: 'Business',
  Practices: 'Businesses',
  acceptingNew: 'accepting new customers',
};

/** The report's name, the same for every business. */
export const DISCOVERY_REPORT_TITLE = 'Discovery Report';

/** Sentence-case form for eyebrows and captions. */
export const DISCOVERY_REPORT_LABEL = 'Discovery report';

/** The report score's name, the same for every business. */
export const DISCOVERY_SCORE_TITLE = 'Discovery Score';

export function getReportVocabulary(isHealthcare: boolean, businessCategory?: string): ReportVocabulary {
  const vocabulary = isHealthcare ? HEALTHCARE_VOCABULARY : BUSINESS_VOCABULARY;
  if (/veterinar|animal hospital|animal clinic/i.test(businessCategory ?? '')) {
    return { ...vocabulary, patients: 'pet owners', patient: 'pet owner', Patients: 'Pet owners', Patient: 'Pet owner' };
  }
  return vocabulary;
}

/** `practice` / `practices` (or `business` / `businesses`) by count. */
export function practiceNoun(vocabulary: ReportVocabulary, count: number): string {
  return count === 1 ? vocabulary.practice : vocabulary.practices;
}
